/**
 * Calcul de l'itinéraire routier d'une trace entière.
 *
 * Une trace d'organisateur suit souvent des chemins, ou relie des points
 * d'intérêt à vol d'oiseau. La rabattre sur le réseau routier donne le trajet
 * qu'un suiveur en voiture peut réellement emprunter.
 *
 * Deux contraintes façonnent le découpage :
 *
 *  - le relais accepte **cent points par requête**, ce qui impose de découper
 *    et de recoudre. Les tronçons se chevauchent d'un point, sans quoi le
 *    raccord laisserait un trou à chaque jointure ;
 *  - une trace peut compter deux cent mille points. Les envoyer tous ferait
 *    deux mille requêtes vers un service public gratuit. On échantillonne donc
 *    avant d'interroger : la forme du parcours suffit à OSRM pour retrouver
 *    les bonnes routes.
 *
 * Référence : `OsrmRouter.getRoute`, `docs/gpx-import-export.md`.
 */

import { requestRoute, type RouteWaypoint } from './osrm'
import { cumulativeDistances } from './elevationFetch'
import type { TrackPoint } from './types'

/** Points par requête, plafond imposé par le relais. */
const CHUNK_SIZE = 100

/**
 * Nombre maximal de points envoyés pour une trace entière.
 *
 * Cinq requêtes au plus. Au-delà, la précision gagnée est nulle — OSRM raccroche
 * de toute façon chaque point à la route la plus proche — et le coût pour un
 * service public gratuit devient déraisonnable.
 */
export const MAX_ROUTE_WAYPOINTS = 500

export interface RouteProgress {
  /** Avancement de 0 à 1. */
  readonly ratio: number
}

export interface RouteThroughOptions {
  readonly profile: string
  readonly signal?: AbortSignal
  readonly onProgress?: (progress: RouteProgress) => void
}

/**
 * Réduit une suite de points à un nombre raisonnable de points de passage.
 *
 * L'échantillonnage porte sur la **distance parcourue** et non sur le rang :
 * une trace dense en ville et clairsemée en rase campagne garderait sinon tous
 * ses points au mauvais endroit.
 */
/**
 * Écart médian au-delà duquel une trace est tenue pour tracée à la main.
 *
 * Un parcours posé à la main sur une carte n'a que les sommets cliqués, reliés
 * par des lignes droites : il coupe les virages au lieu de suivre la route.
 * Mesuré sur le Tour de l'Avenir 2026 — 381 points pour 157 km, un point tous
 * les 400 m — la route réelle s'écarte de la ligne brisée de 8 m en médiane,
 * mais de **27 m au 90ᵉ centile**, au-delà des 25 m de décrochage du snap. En
 * roulant, 12 % du parcours se ferait décroché, en 184 épisodes allant jusqu'à
 * 450 m, et chacun emporte d'un coup l'indicateur kilométrique, le roadbook, le
 * profil et le radar d'alerte. Le kilométrage est sous-estimé de 3 %
 * (156,9 km de cordes contre 162,1 km de route).
 *
 * Le seuil est loin des deux mondes qu'il sépare — 5 à 15 m pour un
 * enregistrement GPS, 300 m et plus pour un tracé main — et n'a donc pas besoin
 * d'être fin.
 */
export const SPARSE_MEDIAN_SPACING_M = 150

/**
 * Nombre de points en deçà duquel la question ne se pose pas.
 *
 * Une poignée de points ne dit rien de la façon dont la trace a été produite,
 * et la médiane y serait dictée par un ou deux écarts.
 */
export const SPARSE_MIN_POINTS = 10

/**
 * Écart **médian** entre points consécutifs, en mètres.
 *
 * La médiane et non la moyenne : un enregistrement où l'on a oublié d'arrêter
 * la trace au retour porte un bond de 100 km, qui suffit à faire passer la
 * moyenne au-dessus du seuil alors que la trace est fine partout où l'on a
 * roulé.
 *
 * Les frontières de segments comptent pour un écart nul, comme partout où les
 * distances cumulées servent : une trace très découpée voit donc sa médiane
 * tirée vers le bas, et peut échapper à la détection. C'est le comportement
 * d'Android, reproduit tel quel.
 */
export function medianPointSpacing(cumulative: ArrayLike<number>): number {
  if (cumulative.length < 2) return 0

  const gaps = new Float64Array(cumulative.length - 1)
  for (let i = 0; i < gaps.length; i++) {
    gaps[i] = cumulative[i + 1]! - cumulative[i]!
  }
  gaps.sort()

  const mid = gaps.length >> 1
  return gaps.length % 2 === 0
    ? (gaps[mid - 1]! + gaps[mid]!) / 2
    : gaps[mid]!
}

/**
 * La trace a-t-elle été tracée à la main plutôt qu'enregistrée ?
 *
 * Quatre conditions, toutes nécessaires : une géométrie, une trace qui n'est
 * pas déjà routée, assez de points pour que la médiane veuille dire quelque
 * chose, et un écart médian **strictement** au-dessus du seuil.
 */
export function isSparseTrack(track: {
  readonly segments: readonly unknown[]
  readonly isRouted: boolean
  readonly allPoints: readonly unknown[]
  readonly cumulativeDistances: ArrayLike<number>
}): boolean {
  return (
    track.segments.length > 0 &&
    !track.isRouted &&
    track.allPoints.length >= SPARSE_MIN_POINTS &&
    medianPointSpacing(track.cumulativeDistances) > SPARSE_MEDIAN_SPACING_M
  )
}

export function sampleWaypoints(
  points: readonly TrackPoint[],
  maxCount = MAX_ROUTE_WAYPOINTS,
): TrackPoint[] {
  if (points.length <= maxCount) return [...points]

  const cumulative = cumulativeDistances(points)
  const total = cumulative[cumulative.length - 1]!
  if (total <= 0) return [points[0]!, points[points.length - 1]!]

  const interval = total / (maxCount - 1)
  const sampled = [points[0]!]
  let nextThreshold = interval

  for (let i = 1; i < points.length - 1; i += 1) {
    if (cumulative[i]! >= nextThreshold) {
      sampled.push(points[i]!)
      nextThreshold = cumulative[i]! + interval
    }
  }

  sampled.push(points[points.length - 1]!)
  return sampled
}

/**
 * Calcule l'itinéraire passant par tous les points donnés.
 *
 * @returns La géométrie complète, ou `null` si un seul tronçon a échoué : un
 *   itinéraire troué est pire qu'aucun itinéraire.
 * @throws Si le calcul est annulé.
 */
export async function routeThrough(
  waypoints: readonly RouteWaypoint[],
  options: RouteThroughOptions,
): Promise<TrackPoint[] | null> {
  if (waypoints.length < 2) return null

  const chunks: RouteWaypoint[][] = []
  for (let start = 0; start < waypoints.length - 1; start += CHUNK_SIZE - 1) {
    chunks.push(waypoints.slice(start, start + CHUNK_SIZE))
  }

  const points: TrackPoint[] = []

  for (const [index, chunk] of chunks.entries()) {
    if (chunk.length < 2) continue

    const result = await requestRoute({
      waypoints: chunk,
      profile: options.profile,
      signal: options.signal,
    })

    if (result === null) return null

    // Le premier point d'un tronçon est le dernier du précédent : le garder
    // dupliquerait un point et créerait un segment de longueur nulle.
    const geometry = result.points.map((point) => ({
      lat: point.lat,
      lon: point.lon,
      ele: null,
      time: null,
      name: null,
    }))

    points.push(...(points.length === 0 ? geometry : geometry.slice(1)))
    options.onProgress?.({ ratio: (index + 1) / chunks.length })
  }

  return points.length >= 2 ? points : null
}
